import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PanelTop, Package, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import { ProgressIndicator, formatCurrency } from '../../components/StockIndicator';
import KapiPencereTable from './KapiPencereTable';
import KapiPencereModal from './KapiPencereModal';
import MontajModal from './MontajModal';
import { kapiPencereKategorileri, getKapiPencereByInsaat } from '@mocks/materials/kapiPencere';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function KapiPencere() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const [filterKategori, setFilterKategori] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isMontajOpen, setIsMontajOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;

    const filteredData = useMemo(() => {
        let data = getKapiPencereByInsaat(insaatId);
        if (filterKategori) data = data.filter(i => i.kategori === filterKategori);
        return data;
    }, [insaatId, filterKategori]);

    const stats = useMemo(() => {
        const toplamAdet = filteredData.reduce((acc, s) => acc + s.miktar, 0);
        const montajlanan = filteredData.reduce((acc, s) => acc + s.montajlanan, 0);
        const bekleyen = filteredData.filter(s => s.durum === 'bekliyor').reduce((acc, s) => acc + s.miktar, 0);
        const toplamMaliyet = filteredData.reduce((acc, s) => acc + s.toplamFiyat, 0);
        return { toplamAdet, montajlanan, bekleyen, toplamMaliyet };
    }, [filteredData]);

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };
    const handleUpdateMontaj = (item) => { setSelectedItem(item); setIsMontajOpen(true); };

    const handleSubmit = async (fd, isEdit) => {
        setIsLoading(true); await new Promise(r => setTimeout(r, 800));
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            addMockCariHareket(fd.supplierId, insaatId || 'c1', { type: 'purchase', subtype: 'fatura', amount: fd.kdvDahilToplam, date: fd.siparisTarihi || new Date().toISOString().split('T')[0], description: fd.autoDescription || `Kapı/Pencere — ${fd.miktar} adet` });
        }
        toast.success(isEdit ? 'Güncellendi' : 'Yeni kayıt eklendi');
        setIsLoading(false); setIsModalOpen(false);
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) { setOdemePlanData({ amount: fd.kdvDahilToplam, companyName: fd.tedarikci }); setIsOdemePlanOpen(true); }
    };
    const handleConfirmDelete = async () => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success('Silindi'); setIsLoading(false); setIsDeleteOpen(false); };
    const handleMontajUpdate = async (md) => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success(`Montaj güncellendi: ${md.montajlanan} adet`); setIsLoading(false); setIsMontajOpen(false); };
    const handleOdemePlanConfirm = ({ nextDueDate }) => { toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`); setIsOdemePlanOpen(false); setOdemePlanData(null); };

    const statsCards = [
        { title: 'Toplam Sipariş', value: `${stats.toplamAdet} adet`, icon: Package, color: 'primary' },
        { title: 'Montajlanan', value: `${stats.montajlanan} adet`, icon: CheckCircle, color: 'success' },
        { title: 'Bekleyen', value: `${stats.bekleyen} adet`, icon: Clock, color: 'warning' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'info' },
    ];

    const detailFields = [{ key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' }, { key: 'kategori', label: 'Kategori' }, { key: 'urunAdi', label: 'Ürün Adı' }, { key: 'ebat', label: 'Ebat' }, { key: 'malzeme', label: 'Malzeme' }, { key: 'miktar', label: 'Sipariş', type: 'number' }, { key: 'montajlanan', label: 'Montajlanan', type: 'number' }, { key: 'toplamFiyat', label: 'Tutar', type: 'currency' }, { key: 'montajDurumu', label: 'Durum', type: 'status' }];

    const headerFilter = (
        <select
            value={filterKategori}
            onChange={(e) => setFilterKategori(e.target.value)}
            className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]"
        >
            <option value="">Tüm Kategoriler</option>
            {kapiPencereKategorileri.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
    );

    return (
        <>
            <MaterialPageLayout title="Kapı / Pencere" subtitle="İnce İşler" icon={PanelTop} iconBgColor="bg-tertiary" backLink="/insaat/ince" stats={statsCards} onAddClick={handleAdd} headerFilter={headerFilter}>
                <div className="p-4 border-b border-border-light bg-bg-tertiary/50"><ProgressIndicator completed={stats.montajlanan} total={stats.toplamAdet} label="Montaj İlerlemesi" /></div>
                <KapiPencereTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} onUpdateMontaj={handleUpdateMontaj} />
            </MaterialPageLayout>
            <KapiPencereModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} data={selectedItem} fields={detailFields} title="Kapı/Pencere Detayı" />
            <MontajModal isOpen={isMontajOpen} onClose={() => setIsMontajOpen(false)} onSubmit={handleMontajUpdate} data={selectedItem} isLoading={isLoading} />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={handleConfirmDelete} title="Kaydı Sil" message={`"${selectedItem?.urunAdi}" kaydını silmek istediğinize emin misiniz?`} isLoading={isLoading} />
            <OdemePlanMiniModal isOpen={isOdemePlanOpen} onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }} onConfirm={handleOdemePlanConfirm} amount={odemePlanData?.amount} companyName={odemePlanData?.companyName} />
        </>
    );
}
